import { styled } from "@mui/material";

export const CenteredMessage = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 200,
});

export const ResizeHandle = styled('span')<{ isResizing: boolean }>(({ theme, isResizing }) => ({
    position: 'absolute',
    right: 0,
    top: '25%',
    height: '50%',
    width: 3,
    cursor: 'col-resize',
    userSelect: 'none',
    touchAction: 'none',
    backgroundColor: isResizing ? theme.palette.primary.main : theme.palette.divider,
    opacity: isResizing ? 1 : 0.5,
    transition: 'opacity 0.15s ease',
    '&:hover': {
        backgroundColor: theme.palette.primary.light,
        opacity: 1,
    },
}));

export const VirtualRow = styled('tr')<{ 
    translateY: number; 
    rowHeight: number; 
    isSelected: boolean;
    isClickable: boolean;
}>(({ translateY, rowHeight, isSelected, isClickable }) => ({
    display: 'flex',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    transform: `translateY(${translateY}px)`,
    height: rowHeight,
    backgroundColor: isSelected ? '#F9FAFB' : 'transparent',
    cursor: isClickable ? 'pointer' : 'default',
    transition: 'background-color 0.15s ease',
    '&:hover': { 
        backgroundColor: '#F9FAFB',
    },
    '&:hover td': {
        backgroundColor: '#F9FAFB',
    },
}));

const VIRTUAL_CELL_NON_FORWARDED_PROPS = new Set([
    '$disableFirstOfTypeStyles',
    '$disableLastOfTypeStyles',
    '$isPinned',
    '$pinnedLeft',
    '$backgroundColor',
    '$allowShadowOverflow',
]);

export const VirtualCell = styled('td', {
    shouldForwardProp: (prop) => !VIRTUAL_CELL_NON_FORWARDED_PROPS.has(prop as string),
})<{
    width?: number | string;
    minWidth?: number | string;
    maxWidth?: number | string;
    $disableFirstOfTypeStyles?: boolean;
    $disableLastOfTypeStyles?: boolean;
    /** Sticks the cell to the left of the scroll container, at $pinnedLeft px */
    $isPinned?: boolean;
    $pinnedLeft?: number;
    /** Opaque background needed so scrolled-under content doesn't show through a pinned cell */
    $backgroundColor?: string;
    /** Lets the pinned-boundary shadow overlay bleed past this cell's right edge instead of being clipped */
    $allowShadowOverflow?: boolean;
}>(({ theme, width, minWidth, maxWidth, $disableFirstOfTypeStyles, $disableLastOfTypeStyles, $isPinned, $pinnedLeft, $backgroundColor, $allowShadowOverflow }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: '0 12px',
    overflow: $allowShadowOverflow ? 'visible' : 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: width ?? 'auto',
    minWidth: minWidth ?? 'auto',
    maxWidth: maxWidth ?? 'none',
    flexShrink: 0,
    backgroundColor: $backgroundColor,
    ...($isPinned && {
        position: 'sticky',
        left: $pinnedLeft ?? 0,
        zIndex: 1,
    }),
    ...(!$disableFirstOfTypeStyles && {
        '&:first-of-type': {
            padding: 0,
            justifyContent: 'center',
        },
    }),
    ...(!$disableLastOfTypeStyles && {
        '&:last-of-type': {
            paddingRight: 20,
        },
    }),
    borderBottom: `1px solid ${theme.palette.divider}`,
}));